/**
 * Contrato del estado de onboarding que el backend expone (hoy servido por un mock en
 * `OnboardingService`, ver TODO ahí). Se respeta el casing tal cual lo envía el backend:
 * snake_case en la mayoría de campos + `isCompleted` en camelCase.
 *
 * Los campos que el diseño (UI_SPEC.md) necesita para renderizar son obligatorios. `completed_detail`
 * queda opcional porque solo aplica cuando `isCompleted === true`. El backend debe confirmar que
 * `state$` emitirá `subtitle`, `time_left_label`, `time_label`, `tasks_done`/`tasks_total`,
 * `completed_detail` y la clave `module`; hasta entonces el mock cubre el contrato.
 */
export interface OnboardingState {
    onboarding_id: string;
    version: number;
    /** Descripción del flujo; la envía el backend (contrato base). No la consume la UI. */
    description?: string;
    /**
     * Fase activa según el backend (fuente de verdad). El backend calcula el avance de fases
     * (obligatorias completas o vencimiento de `duration_days`) y expone aquí el `phase_id` vigente.
     * El front NO recalcula: selecciona la fase activa con este valor.
     */
    currentPhaseId: string;
    progress: {
        mandatory: { global: number; byPhase: Record<string, number> };
        overall: { global: number; byPhase: Record<string, number> };
    };
    phases: OnboardingPhase[];
    /**
     * Cierre del onboarding (FINA-3377). Lo calcula el backend: `completed` oculta banner y navbar, y
     * `showModal` indica que el modal final está pendiente (se abre solo si el claim atómico lo confirma).
     * Opcional por compatibilidad: sin este bloque se usa el criterio previo (`overall.global >= 100`).
     */
    completion?: OnboardingCompletion;
}

/** Estado de cierre del onboarding que envía el backend (`GET /api/onboarding/status` y socket). */
export interface OnboardingCompletion {
    completed: boolean;
    completedAt: string | null;
    source: 'natural' | 'migrated' | null;
    showModal: boolean;
    daysToComplete: number | null;
    rubro: string | null;
}

/** Estado de una fase según el backend: ya completada, la activa, o aún bloqueada. */
export type OnboardingPhaseStatus = 'completed' | 'active' | 'locked';

/**
 * Paso del tour guiado tal como lo define el Blueprint del backend (snake_case).
 * `target_selector` apunta al ancla del DOM (id o data-tour). Consumido a futuro por
 * el `TourOrchestratorService`/`OnboardingBlackoutComponent`.
 */
export interface OnboardingGuidedTourStep {
    step_index: number;
    target_selector: string;
    title: string;
    content: string;
    placement: string;
}

export interface OnboardingGuidedTour {
    auto_start: boolean;
    steps: OnboardingGuidedTourStep[];
}

/** Layout visual de una fase: tarjetas verticales (Fase 1) o barras horizontales (Fase 2). */
export type OnboardingLayout = 'vertical' | 'horizontal';

/** Tema de color de la fase (Fase 3 usa verde en vez del púrpura de marca). */
export type OnboardingTheme = 'purple' | 'green';

export interface OnboardingPhase {
    phase_id: string;
    title: string;
    order: number;
    timeframe_label: string;
    /** Estado calculado por el backend ('completed' | 'active' | 'locked'). Opcional: fail-open si un payload no lo trae. */
    status?: OnboardingPhaseStatus;
    /** Layout de la fase. El front lo respeta tal cual (sin ramas por `phase_id`/`order`). */
    layout: OnboardingLayout;
    /** Tema de color (default `'purple'` si no viene). */
    theme?: OnboardingTheme;
    /** Sustantivo del contador del header. Ej. `'reportes explorados'` → "0 de 4 reportes explorados". */
    counter_label?: string;
    progress: {
        mandatory: number; // 0..100
        overall: number; // 0..100 -> alimenta el anillo
    };
    /** Header: subtítulo bajo el título. Figma: "Empecemos." */
    subtitle: string;
    /** Header: etiqueta de tiempo restante junto al título. Figma: "~ 9 min left" */
    time_left_label: string;
    /** Conteo para el sub-label del anillo: "{done}/{total} Done". */
    tasks_done: number;
    tasks_total: number;
    /** Contenido del modal de celebración al completar la fase (opcional). */
    celebration_config?: OnboardingCelebrationConfig;
    tasks: OnboardingTask[];
}

/** Contenido (data-driven) del modal de celebración que se muestra al completar una fase. */
export interface OnboardingCelebrationConfig {
    eyebrow: string;
    title: string;
    /** Opcional: algunas fases (ej. Fase 1) no muestran subtítulo en el modal de celebración. */
    subtitle?: string;
    /** Métricas estáticas (legado). El modal ahora muestra una sola métrica derivada del `state$`. */
    metrics?: OnboardingCelebrationMetric[];
    primary_cta: { label: string; link: string };
    secondary_cta?: { label: string; action: 'dismiss' };
}

export interface OnboardingCelebrationMetric {
    value: string;
    label: string;
    /** Badge opcional junto al label (ej. "próx.", "total"). */
    highlight?: string;
}

export interface OnboardingTask {
    task_id: string;
    title: string;
    /** Subtítulo del backend (contrato base). Suele venir vacío; no lo usa la UI. */
    subtitle?: string;
    /** Clave de negocio del módulo; deriva el ícono vía `resolveModuleIcon`. */
    module: OnboardingModule;
    link: string;
    is_mandatory: boolean;
    isCompleted: boolean;
    /** Línea gris bajo el título (layout horizontal). Figma: "Crea listas de precios". */
    description?: string;
    /** Recompensa en XP; el front la formatea como "+{n} XP" (solo completadas, layout horizontal). */
    reward_xp?: number;
    /** Texto del botón de acción de la tarjeta. Default `'Ir'`. Ej. Fase 3 usa `'Ver'`. */
    action_label?: string;
    /** Tarjeta no-completada: "⧖ 2 min". */
    time_label: string;
    /**
     * Tarjeta completada: caja gris de detalle.
     *   label -> "100 productos agregados" (línea superior, oscura)
     *   value -> "3000.00 USD"             (línea inferior, gris)
     * Obligatorio solo cuando `isCompleted === true`.
     */
    completed_detail?: { label: string; value: string };
    /** Tour guiado del Blueprint (auto_start + pasos con target_selector). Integración futura con el orquestador. */
    guided_tour?: OnboardingGuidedTour;
}

/** Claves de módulo conocidas (snake_case, alineadas con el backend). */
export type OnboardingModule =
    | 'empresa' // creación de cuenta / datos de empresa
    | 'producto' // inventario / primer producto
    | 'cuenta_bancaria' // crear y fondear cuenta
    | 'venta' // primera venta / POS
    // Fase 2
    | 'canales' // listas de precios / canales
    | 'combos' // combos y promociones
    | 'usuarios' // usuarios asociados / equipo
    | 'gastos' // gastos
    | 'caja' // apertura/cierre de caja
    | 'devoluciones' // devoluciones
    // Fase 3 (reportes)
    | 'resumen_financiero' // resumen financiero
    | 'utilidad' // utilidad vs facturación
    | 'estadisticas' // estadísticas de ventas
    | 'descarga' // descargar reporte
    | string; // fallback tolerante a módulos futuros

/**
 * Mapa módulo -> nombre de ícono del sistema de íconos del proyecto (FontAwesome solid `iconName`).
 * Un módulo nuevo solo requiere una entrada acá; el ícono debe estar registrado en
 * `src/modules/icons/icons.font-awesome-solid.ts`.
 */
export const MODULE_ICON_MAP: Record<string, string> = {
    // Fase 1
    empresa: 'store', // tienda / negocio
    producto: 'box', // caja / paquete  (Figma: "Carga tu primer producto")
    cuenta_bancaria: 'credit-card', // tarjeta         (Figma: "Crear y fondear cuenta")
    venta: 'cash-register', // POS / recibo    (Figma: "Realiza tu primera venta")
    // Fase 2
    canales: 'sliders', // listas de precios
    combos: 'shapes', // combos y promociones
    usuarios: 'users', // usuarios asociados
    gastos: 'file-lines', // gastos
    caja: 'cash-register', // apertura/cierre de caja
    devoluciones: 'reply', // devoluciones
    // Fase 3
    resumen_financiero: 'database', // resumen financiero
    utilidad: 'chart-bar', // utilidad vs facturación
    estadisticas: 'chart-line', // estadísticas de ventas
    descarga: 'download', // descargar reporte
};

/** Ícono por defecto si el módulo no está mapeado (no romper el render). */
export const DEFAULT_MODULE_ICON = 'box';

export function resolveModuleIcon(module: string): string {
    return MODULE_ICON_MAP[module] ?? DEFAULT_MODULE_ICON;
}

export type TaskCardVariant = 'completed' | 'next' | 'pending';

/**
 * `next` = primera tarea no completada de la fase activa (el "Siguiente paso"). El componente padre
 * calcula el índice de esa tarea y se lo pasa a cada tarjeta.
 */
export function resolveVariant(task: OnboardingTask, isNext: boolean): TaskCardVariant {
    if (task.isCompleted) {
        return 'completed';
    }
    return isNext ? 'next' : 'pending';
}
